const UPSERT_TASK = 'UPSERT_TASK'
const PATCH_TASK = 'PATCH_TASK'
const REMOVE_TASK = 'REMOVE_TASK'

export const TASK_STATES = {
  PROGRESS: 'progress',
  SUCCESS: 'success',
  ERROR: 'error',
}

// Retry callbacks stay outside the store so its state remains plain, serialisable data.
const retryHandlersByTaskId = new Map()
let lastRunId = 0

// Exported so a card kept outside this store still takes its place in the stack's start order.
export const nextBackgroundTaskRunId = () => {
  lastRunId += 1
  return lastRunId
}

const _state = {
  tasks: [],
}

const _getters = {
  tasks: (state) => state.tasks,
  findTask: (state) => (taskId) => state.tasks.find((task) => task.id === taskId),
  isTaskRunning: (state, getters) => (taskId) => getters.findTask(taskId)?.state === TASK_STATES.PROGRESS,
}

const actions = {
  // `value` stays null until the backend reports a real percentage.
  startBackgroundTaskAction({ commit }, {
    id, kind = id, fileName, retry,
  }) {
    const runId = nextBackgroundTaskRunId()
    if (retry) {
      retryHandlersByTaskId.set(id, retry)
    } else {
      retryHandlersByTaskId.delete(id)
    }
    commit(UPSERT_TASK, {
      id,
      kind,
      fileName,
      state: TASK_STATES.PROGRESS,
      value: null,
      downloadUrl: '',
      runId,
    })
  },

  updateBackgroundTaskProgressAction({ commit, getters }, { id, value }) {
    if (!getters.isTaskRunning(id)) return
    const percent = Math.min(Math.max(Math.round(Number(value) || 0), 0), 100)
    commit(PATCH_TASK, { id, value: percent })
  },

  completeBackgroundTaskAction({ commit, getters }, { id, downloadUrl = '' }) {
    const task = getters.findTask(id)
    if (!task || task.state !== TASK_STATES.PROGRESS) return
    commit(PATCH_TASK, {
      id, state: TASK_STATES.SUCCESS, value: 100, downloadUrl,
    })
  },

  failBackgroundTaskAction({ commit, getters }, { id }) {
    if (!getters.isTaskRunning(id)) return
    commit(PATCH_TASK, { id, state: TASK_STATES.ERROR })
  },

  dismissBackgroundTaskAction({ commit }, id) {
    retryHandlersByTaskId.delete(id)
    commit(REMOVE_TASK, id)
  },

  retryBackgroundTaskAction({ dispatch }, id) {
    const retry = retryHandlersByTaskId.get(id)
    dispatch('dismissBackgroundTaskAction', id)
    if (retry) retry()
  },
}

const replaceTasks = (state, nextTasks) => state.tasks.splice(0, state.tasks.length, ...nextTasks)

const mutations = {
  [UPSERT_TASK](state, task) {
    replaceTasks(state, [...state.tasks.filter((existing) => existing.id !== task.id), task])
  },
  [PATCH_TASK](state, { id, ...changes }) {
    replaceTasks(state, state.tasks.map((task) => (task.id === id ? { ...task, ...changes } : task)))
  },
  [REMOVE_TASK](state, id) {
    replaceTasks(state, state.tasks.filter((task) => task.id !== id))
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
